import { Skeleton, Box, Typography } from "@mui/material";
import { GridFilterModel } from "@mui/x-data-grid";
import { useState, useEffect, useRef, useContext } from "react";
import DataGridCampaigns from "src/components/data-grid/activations/data-grid-campaigns";
import Iconify from "src/components/iconify";
import { CountryFilter, useCountryData } from "src/components/visibility";
import { VisibilityContext } from "src/components/visibility/components/VisibilityProvider";
import { useGetCampaignsQuery } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import countriesJson from "src/utils/countries.json";

export default function ActivationsCampaign() {
    const workspace: any = useCurrentWorkspace();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [sortModel, setSortModel] = useState<any>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: []
    });
    const [localPatches, setLocalPatches] = useState({});
    const { countriesData, isLoading } = useCountryData();
    const [selectedCountry, setSelectedCountry]: any = useState<string | null>(null);
    const ciContext = useContext(VisibilityContext);
    const prefilledFromQueryRef = useRef<boolean>(false);

    const [selectedDateRange, setSelectedDateRange] = useState({
        label: "Last 7 Days",
        value: "last7d"
    });

    const [selectedCompareDate, setSelectedCompareDate] = useState({
        label: "Prev. Period",
        value: "previous_period"
    });


    const handleDateRangeChange = (newRange: { label: string; value: string }) => {
        setSelectedDateRange(newRange);
    }

    const handleCompareDateChange = (newRange: { label: string; value: string }) => {
        setSelectedCompareDate(newRange);
    }

    useEffect(() => {
        if (ciContext?.selectedCountry != null) {
            setSelectedCountry(ciContext.selectedCountry);
            return;
        }

        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country') || params.get('c');

        if (codeParam && countriesData.length > 0) {
            const mapped = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
            if (mapped && countriesData.some((c) => c.country_name === mapped)) {
                prefilledFromQueryRef.current = true;
                setSelectedCountry(mapped);
                return;
            }
        }

        if (!prefilledFromQueryRef.current && !selectedCountry && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }

    }, [ciContext?.selectedCountry, location.search, countriesData]);


    const { data: campaigns, isFetching, refetch } = useGetCampaignsQuery(
        {
            client: workspace?.bqTable,
            page: page + 1,
            page_size: pageSize,
            workspace_id: workspace?.id,
            match_type: "EXACT",
            country: selectedCountry,
            sort: sortModel,
            filters: filterModel,
            date: selectedDateRange?.value || "last30d",
            compareDate: selectedCompareDate?.value
        },
        {
            skip: !workspace || !selectedCountry, refetchOnMountOrArgChange: true
        }
    );

    const isReady = !!workspace && countriesData.length > 0 && selectedCountry !== null;

    return (
        <>
            <Box>
                <CountryFilter
                    loading={isLoading}
                    countries={countriesData}
                    selectedCountry={selectedCountry}
                    onCountryChange={(c) => {
                        setSelectedCountry(c);
                        try { ciContext?.setSelectedCountry?.(c); } catch { }
                        try {
                            if (workspace?.id) {
                                const key = `ci.selectedCountry.${workspace.id}`;
                                if (c) localStorage.setItem(key, c);
                                else localStorage.removeItem(key);
                            }
                        } catch { }
                    }}
                />

                <Box px={'24px'} mt={isLoading ? '24px' : (countriesData && countriesData?.length > 1) ? '24px' : '0px'}>
                    <Box
                        bgcolor="white"
                        sx={{
                            border: "1px solid rgba(145, 158, 171, 0.20)",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "column",
                            height: '720px'
                        }}
                    >
                        {!isReady || isFetching ? (
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    flex: 1,
                                    width: "100%",
                                    borderRadius: "8px",
                                }}
                            />
                        ) : (
                            <DataGridCampaigns
                                data={campaigns?.data}
                                rowCount={campaigns?.total}
                                refetch={refetch}
                                page={page}
                                pageSize={pageSize}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                sortModel={sortModel}
                                setSortModel={setSortModel}
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                localPatches={localPatches}
                                setLocalPatches={setLocalPatches}
                                selectedDateRange={selectedDateRange}
                                selectedCompareDate={selectedCompareDate}
                                onDateRangeChange={handleDateRangeChange}
                                onCompareDateChange={handleCompareDateChange}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
}
